import { useMemo, useState, type ReactElement, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { boardPath, type BoardGroup, type BoardPageBoard } from '@molt/shared';
import { Check, ChevronDown, Search, SquareKanban } from 'lucide-react';
import { Button } from '@/components/Button';
import { Combobox, type ComboboxGroup, type ComboboxOption } from '@/components/Combobox';
import { EmptyState } from '@/components/EmptyState';
import { Notice } from '@/components/Notice';
import { Popover } from '@/components/Popover';
import { Skeleton } from '@/components/Skeleton';
import { boardsPageQueryOptions, filterGroupsByName } from '@/lib/boards-page';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

/**
 * The shell's board switcher — `FR-BOARD-004` ("starred boards appear … in the
 * board switcher") and `FR-EXP-017`'s `B`. Mounted in `Header`'s
 * `boardSwitcherSlot` beside the workspace switcher.
 *
 * ── Popover + Combobox, not Menu ────────────────────────────────────────────
 * A searchable list is the `Menu.tsx` header's named exception: a `menuitem`
 * may not contain a text input. The Combobox runs `inline` because `Popover`'s
 * body would clip its default absolutely positioned panel.
 *
 * ── Data ───────────────────────────────────────────────────────────────────
 * `GET /boards?state=open` under the landing page's own key, so the two share
 * one cache entry and every star/recent-view invalidation already reaches this.
 * The query is enabled only while the switcher is open: the header mounts on
 * every route, and a board list per navigation is not its cost to impose.
 * Templates are listed, as the landing page lists them.
 *
 * ── One board, several rows ─────────────────────────────────────────────────
 * With an empty filter a board can sit under Starred, Recent AND its
 * workspace. Each occurrence is its own option object with a group-prefixed
 * id: `aria-activedescendant` needs unique ids, and Combobox locates the
 * active row with `indexOf`, which compares object identity. A non-empty
 * filter drops Starred and Recent, so each board appears once.
 *
 * ── The current board ──────────────────────────────────────────────────────
 * Marked with a check and a visually hidden "Current board", inside the row:
 * Combobox's `aria-selected` already means "the active descendant", so it is
 * not overloaded here. Choosing the current board only closes.
 */

export interface BoardSwitcherProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The `/b/:shortLink` short link on screen, or `null` off a board route. */
  currentBoardShortLink: string | null;
  /** A callback, not a `Link`: Enter on an active option clicks nothing. */
  onNavigate: (href: string) => void;
  triggerRef?: RefObject<HTMLButtonElement | null> | undefined;
  className?: string;
}

interface BoardOption extends ComboboxOption {
  readonly board: BoardPageBoard;
  readonly title: string;
  readonly context: string | null;
  readonly current: boolean;
}

type Translate = InterfaceLanguageContextValue['t'];

const PREFETCH_STALE_MS = 30_000;

function boardTitle(t: Translate, board: BoardPageBoard): string {
  return board.title === '' ? t('boardSwitcher.untitledBoard') : board.title;
}

function workspaceTitle(t: Translate, name: string): string {
  return name === '' ? t('boardSwitcher.untitledWorkspace') : name;
}

export function buildBoardSwitcherGroups(
  data: {
    starred: readonly BoardPageBoard[];
    recent: readonly BoardPageBoard[];
    groups: readonly BoardGroup[];
  },
  query: string,
  currentBoardShortLink: string | null,
  t: Translate,
): ComboboxGroup<BoardOption>[] {
  const workspaceNames = new Map(
    data.groups.map((group) => [
      group.workspaceId,
      workspaceTitle(t, group.workspaceName),
    ]),
  );

  function option(
    groupId: string,
    board: BoardPageBoard,
    withContext: boolean,
  ): BoardOption {
    const title = boardTitle(t, board);
    return {
      id: `${groupId}-${board.id}`,
      label: title,
      board,
      title,
      context: withContext ? (workspaceNames.get(board.workspaceId) ?? null) : null,
      current: board.shortLink === currentBoardShortLink,
    };
  }

  function workspaceGroups(groups: readonly BoardGroup[]): ComboboxGroup<BoardOption>[] {
    return groups.map((group) => {
      const id = `workspace-${group.workspaceId}`;
      return {
        id,
        label: workspaceTitle(t, group.workspaceName),
        options: group.boards.map((board) => option(id, board, false)),
      };
    });
  }

  if (query.trim() !== '') {
    return workspaceGroups(filterGroupsByName(data.groups, query));
  }

  return [
    {
      id: 'starred',
      label: t('boardSwitcher.starredGroup'),
      options: data.starred.map((board) => option('starred', board, true)),
    },
    {
      id: 'recent',
      label: t('boardSwitcher.recentGroup'),
      options: data.recent.map((board) => option('recent', board, true)),
    },
    ...workspaceGroups(data.groups),
  ];
}

export function BoardSwitcher({
  open,
  onOpenChange,
  currentBoardShortLink,
  onNavigate,
  triggerRef,
  className,
}: BoardSwitcherProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const boards = useQuery({ ...boardsPageQueryOptions('open'), enabled: open });
  const trimmed = value.trim();

  const groups = useMemo(
    () =>
      boards.data === undefined
        ? []
        : buildBoardSwitcherGroups(boards.data, value, currentBoardShortLink, t),
    [boards.data, value, currentBoardShortLink, t],
  );
  const firstOption = groups.find((group) => group.options.length > 0)?.options[0];

  function setOpen(next: boolean): void {
    // A reopened switcher starts unfiltered; a stale filter would hide boards.
    if (!next) setValue('');
    onOpenChange(next);
  }

  function choose(option: BoardOption): void {
    setOpen(false);
    if (option.current) return;
    onNavigate(boardPath(option.board.shortLink, option.board.slug));
  }

  const emptyState = boards.isPending ? (
    <div aria-label={t('boardSwitcher.loading')} aria-busy="true">
      <Skeleton shape="row" count={3} />
    </div>
  ) : boards.isError ? (
    <Notice
      tone="bad"
      title={t('boardSwitcher.loadFailedTitle')}
      body={t('boardSwitcher.loadFailedBody')}
      action={
        <Button type="button" size="sm" onClick={() => void boards.refetch()}>
          {t('boardSwitcher.retry')}
        </Button>
      }
    />
  ) : trimmed !== '' ? (
    <EmptyState
      size="inline"
      tone="neutral"
      title={t('boardSwitcher.noMatches', { query: trimmed })}
    />
  ) : (
    <EmptyState size="inline" tone="neutral" title={t('boardSwitcher.noBoards')} />
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      title={t('boardSwitcher.title')}
      width="min(20rem, calc(100vw - 2rem))"
      trigger={
        <button
          ref={triggerRef}
          type="button"
          aria-label={t('boardSwitcher.trigger')}
          onPointerEnter={() => {
            void queryClient.prefetchQuery({
              ...boardsPageQueryOptions('open'),
              staleTime: PREFETCH_STALE_MS,
            });
          }}
          className={cn(
            'flex h-9 min-w-0 shrink-0 items-center gap-1.5 rounded-card px-2 text-card text-ink',
            'hover:bg-surface-raised',
            className,
          )}
        >
          <SquareKanban aria-hidden="true" className="size-4 shrink-0 text-ink-muted" />
          {/* Visible label only, hidden below 640px; the name stays on the button. */}
          <span aria-hidden="true" className="hidden sm:inline-block">
            {t('boardSwitcher.triggerLabel')}
          </span>
          <ChevronDown
            aria-hidden="true"
            className="hidden size-3.5 shrink-0 text-ink-muted sm:block"
          />
        </button>
      }
    >
      <Combobox<BoardOption>
        inline
        openWhenEmpty
        // Radix focuses the first tabbable on open, which is this input; the
        // attribute keeps that true if a close control is ever added above it.
        autoFocus
        label={t('boardSwitcher.filterLabel')}
        hideLabel
        placeholder={t('boardSwitcher.filterPlaceholder')}
        value={value}
        onChange={setValue}
        groups={groups}
        loading={boards.isFetching}
        loadingLabel={t('boardSwitcher.loading')}
        emptyState={emptyState}
        leadingIcon={<Search aria-hidden="true" className="size-4" />}
        onActivate={choose}
        onSubmit={() => {
          // Enter with nothing highlighted opens the first MATCH — only once
          // something is typed, never the first Starred board by default.
          if (trimmed !== '' && firstOption !== undefined) choose(firstOption);
        }}
        renderOption={(option) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="block min-w-0 flex-1">
              <span className="block truncate font-medium">{option.title}</span>
              {/* Spaces between the spans keep the option's accessible name
                  from running words together; flex and block layout drop them. */}
              {option.context === null ? null : ' '}
              {option.context === null ? null : (
                <span className="block truncate text-xs text-current opacity-75">
                  {option.context}
                </span>
              )}
            </span>
            {option.current ? (
              <>
                {' '}
                <Check aria-hidden="true" className="size-4 shrink-0" />
                <span className="sr-only">{t('boardSwitcher.current')}</span>
              </>
            ) : null}
          </span>
        )}
      />
    </Popover>
  );
}
